<script setup>
import {ref, onMounted, computed} from 'vue';
import {useRoute} from 'vue-router';
import {useToast} from "primevue/usetoast";
import {useSensorStore} from '@/store/SensorStore';
import {useSettingStore} from "@/store/SettingsStore";
import {getUnitLabel, formatTimestamp, getStatusClass} from "@/plugins/utils";
import {useFetch} from "@/plugins/fetch";


// Import components
import ArgonButton from "@/components/Argon/ArgonButton.vue";
import GradientGraph from "@/components/Data/GradientGraph.vue";
import SensorSharingModal from "@/components/Sensors/SensorSharingModal.vue";
import AccessLevelBadge from "@/components/Sensors/AccessLevelBadge.vue";
import MultiSelect from "primevue/multiselect";
import Skeleton from "primevue/skeleton";

// Initialize stores and plugins
const route = useRoute();
const toast = useToast();
const fetch = useFetch();
const sensorStore = useSensorStore();
const settingsStore = useSettingStore();

// Component-specific state
const sharingModalVisible = ref(false);

// Computed properties for data binding
const sensor = computed(() => sensorStore.currentSensor);
const isLoading = computed(() => sensorStore.isLoadingDetails);

const sensorName = computed({
  get: () => sensor.value?.name || '',
  set: (value) => {
    if (sensor.value) sensor.value.name = value;
  }
});

const sensorLocation = computed({
  get: () => sensor.value?.location || '',
  set: (value) => {
    if (sensor.value) sensor.value.location = value;
  }
});

const enabled = computed({
  get: () => sensor.value?.enabled || false,
  set: (value) => {
    if (sensor.value) sensor.value.enabled = value;
  }
});

const fetchData = async () => {
  fetch(`api/sensors/${sensor.value.unique_id}/chart-data`)
    .then(response => response.json())
    .then(data => {
      sensor.value.chartData = data;
    })
    .catch(error => {
      console.error("Error fetching chart data:", error);
      toast.add({severity: 'error', summary: 'Error', detail: 'Failed to load chart data', life: 3000});
    });

  fetch(`api/sensors/${sensor.value.unique_id}/latest`)
    .then(response => response.json())
    .then(data => {
      sensor.value.latestData = data;
    })
    .catch(error => {
      console.error("Error fetching latest data:", error);
      toast.add({severity: 'error', summary: 'Error', detail: 'Failed to load latest data', life: 3000});
    });
}

// Computed properties for chart and live data
const tempData = computed(() => sensor.value?.chartData?.temperatures || []);
const humData = computed(() => sensor.value?.chartData?.humidities || []);
const chartLabels = computed(() => sensor.value?.chartData?.labels || []);
const latestSensorData = computed(() => sensor.value?.latestData || []);

// --- DATA FETCHING ---
onMounted(async () => {
  await sensorStore.fetchSensorById(route.params.unique_id);
  await fetchData();
});

// --- METHODS ---
const updateSensor = async () => {
  if (!sensor.value) return;
  try {
    const payload = {
      name: sensorName.value,
      location: sensorLocation.value,
      enabled: enabled.value,
    };
    await fetch(`sensors/${sensor.value.unique_id}/`, {
      method: 'PUT',
      body: payload,
    });
    toast.add({severity: 'success', summary: 'Success', detail: 'Sensor details updated', life: 3000});
  } catch {
    toast.add({severity: 'error', summary: 'Error', detail: 'Failed to update sensor', life: 3000});
  }
};

const resetForm = () => {
  sensorStore.fetchSensorById(route.params.unique_id);
  toast.add({severity: 'info', summary: 'Info', detail: 'Changes were reset', life: 3000});
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text).then(() => {
    toast.add({severity: 'success', summary: 'Copied', detail: 'Copied to clipboard!', life: 2000});
  });
};

</script>
<template>
  <div class="py-4 container-fluid">
    <Toast/>

    <!-- Loading Skeleton -->
    <div v-if="isLoading">
      <div class="row">
        <div class="col-lg-8">
          <Skeleton height="30rem"/>
        </div>
        <div class="col-lg-4">
          <Skeleton height="30rem"/>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else-if="sensor" class="main-content">
      <!-- Header -->
      <div class="sensor-hero mb-4">
        <div class="sensor-hero__top">
          <div class="sensor-identity">
            <div class="sensor-icon">
              <i class="fas fa-satellite-dish"></i>
            </div>
            <div>
              <div class="sensor-name-row">
                <h5 class="mb-0">{{ sensorName }}</h5>
                <span class="badge status-chip" :class="getStatusClass(sensor.status)">{{ sensor.status }}</span>
              </div>
              <p class="text-sm mb-0"><i class="fas fa-map-marker-alt me-1"></i>{{ sensorLocation || 'No location set' }}</p>
            </div>
          </div>
          <div class="sensor-meta">
            <div class="meta-block">
              <span class="label">Type</span>
              <strong>{{ sensor.sensor_type_label || sensor.sensor_type || 'Unknown' }}</strong>
            </div>
            <div class="meta-block">
              <span class="label">Network</span>
              <strong>{{ sensor.network_ssid || 'Not reported' }}</strong>
            </div>
            <div class="meta-block">
              <span class="label">Last Seen</span>
              <strong>{{ formatTimestamp(sensor.last_seen) || 'Never' }}</strong>
            </div>
          </div>
        </div>
        <div class="sensor-hero__bottom">
          <div class="pill"><i class="fas fa-microchip me-1"></i> ID: {{ sensor.unique_id }}</div>
          <div class="pill" v-if="sensor.firmware_version"><i class="fas fa-code-branch me-1"></i> FW {{ sensor.firmware_version }}</div>
          <div class="pill" v-if="sensor.wifi_rssi"><i class="fas fa-wifi me-1"></i> Signal {{ sensor.wifi_rssi }} dBm</div>
        </div>
      </div>

      <div class="row">
        <!-- Left Column -->
        <div class="col-lg-8">
          <!-- Graph Card -->
          <div class="card">
            <div class="card-header pb-0">
              <h6>Sensor History</h6>
            </div>
            <div class="card-body p-3">
              <GradientGraph
                  v-if="tempData.length && humData.length"
                  id="sensor-chart"
                  :height="300"
                  :temperatureData="tempData"
                  :humidityData="humData"
                  :labels="chartLabels"
              />
              <p v-else class="text-center text-muted py-5">Not enough data to display graph.</p>
            </div>
          </div>

          <!-- Settings Card -->
          <div class="card mt-4">
            <div class="card-header pb-0">
              <h6>Settings</h6>
            </div>
            <div class="card-body">
              <form @submit.prevent="updateSensor">
                <div class="row">
                  <div class="col-md-6">
                    <label for="sensorName" class="form-label">Sensor Name</label>
                    <input id="sensorName" type="text" class="form-control" v-model="sensorName">
                  </div>
                  <div class="col-md-6">
                    <label for="sensorLocation" class="form-label">Sensor Location</label>
                    <input id="sensorLocation" type="text" class="form-control" v-model="sensorLocation">
                  </div>
                </div>
                <div class="form-group mt-3">
                  <label>Tags</label>
                  <MultiSelect class="form-control" placeholder="Select or add tags" display="chip"/>
                </div>
                <div class="form-group form-check form-switch mt-4">
                  <input id="enabled-switch" class="form-check-input" type="checkbox" v-model="enabled">
                  <label class="form-check-label" for="enabled-switch">Sensor Enabled</label>
                </div>
                <div class="mt-4 d-flex justify-content-end">
                  <ArgonButton type="button" color="light" class="m-0" @click="resetForm">Cancel</ArgonButton>
                  <ArgonButton type="submit" color="success" class="m-0 ms-2">Save Changes</ArgonButton>
                </div>
              </form>
            </div>
          </div>
        </div>

        <!-- Right Column -->
        <div class="col-lg-4 mt-4 mt-lg-0">
          <!-- Live Data Card -->
          <div class="card">
            <div class="card-header pb-0">
              <h6>Live Data</h6>
            </div>
            <div class="card-body pt-0">
              <div v-if="latestSensorData.length" class="row">
                <div v-for="data in latestSensorData" :key="data.data_type" class="col-12">
                  <div class="live-data-item">
                    <div class="d-flex justify-content-between align-items-baseline">
                      <p class="text-sm mb-0 text-capitalize">{{ data.data_type }}</p>
                      <p class="text-xs mb-0 text-muted">{{ formatTimestamp(data.timestamp) }}</p>
                    </div>
                    <p class="h4 font-weight-bold mb-0">
                      {{ data.value }}
                      <span class="text-lg font-weight-normal">{{
                          getUnitLabel(data.data_type, settingsStore.isMetricUnits)
                        }}</span>
                    </p>
                  </div>
                </div>
              </div>
              <p v-else class="text-center text-muted py-3">No recent data available.</p>
            </div>
          </div>

          <!-- Status & Actions Card -->
          <div class="card mt-4">
            <div class="card-header pb-0">
              <h6>Status & Actions</h6>
            </div>
            <div class="card-body">
              <div class="d-flex align-items-center mb-3">
                <AccessLevelBadge :level="sensor.access_level"/>
                <ArgonButton v-if="['OWNER', 'ADMIN'].includes(sensor.access_level)" size="sm" color="info"
                             variant="outline" class="ms-auto" @click="sharingModalVisible = true">
                  <i class="fas fa-share-alt me-1"></i> Share
                </ArgonButton>
              </div>
              <ul class="list-group">
                <li class="list-group-item border-0 ps-0 pt-0 text-sm"><strong class="text-dark">Last Seen:</strong>
                  &nbsp; {{ formatTimestamp(sensor.last_seen) }}
                </li>
                <li class="list-group-item border-0 ps-0 text-sm"><strong class="text-dark">Registration Code:</strong>
                  &nbsp; {{ sensor.unique_id }}
                </li>
                <li class="list-group-item border-0 ps-0 pb-0">
                  <strong class="text-dark">API Key:</strong>
                  <div class="input-group">
                    <input type="text" class="form-control form-control-sm" :value="sensor.api_key" readonly>
                    <ArgonButton type="button" color="secondary" size="sm" class="mb-0"
                                 @click="copyToClipboard(sensor.api_key)"><i class="fas fa-copy"></i></ArgonButton>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fallback for sensor not found -->
    <div v-else class="text-center mt-5">
      <h4>Sensor not found</h4>
      <p>The sensor with ID `{{ route.params.unique_id }}` could not be retrieved.</p>
    </div>

    <SensorSharingModal v-if="sensor" v-model:visible="sharingModalVisible" :sensor-id="sensor.unique_id"/>
  </div>
</template>

<style scoped>
.live-data-item {
  padding: 1rem 0;
  border-bottom: 1px solid var(--bs-gray-200);
}

.live-data-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.dark-version .live-data-item {
  border-color: var(--bs-gray-700);
}

.p-multiselect {
  width: 100%;
}

.sensor-hero {
  background: linear-gradient(135deg, rgba(0,212,255,0.12), rgba(15,118,110,0.14));
  border: 1px solid rgba(0,212,255,0.18);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  color: var(--calid-text-primary, #0f172a);
  box-shadow: 0 18px 35px rgba(0,0,0,0.14);
}

.sensor-hero__top {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.sensor-hero__bottom {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.9rem;
}

.sensor-identity {
  display: flex;
  gap: 0.9rem;
  align-items: center;
}

.sensor-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255,255,255,0.24);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0ea5e9;
  font-size: 1.35rem;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.45);
}

.sensor-name-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.sensor-name-row h5 {
  margin: 0;
  font-weight: 700;
  color: #0f172a;
}

.status-chip {
  text-transform: capitalize;
}

.sensor-hero .text-sm {
  color: rgba(15,23,42,0.8);
}

.sensor-meta {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 0.75rem;
}

.meta-block {
  background: rgba(255,255,255,0.22);
  border: 1px solid rgba(255,255,255,0.32);
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  color: #0f172a;
}

.meta-block .label {
  display: block;
  font-size: 0.8rem;
  color: rgba(15,23,42,0.7);
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  background: rgba(15,23,42,0.08);
  border-radius: 999px;
  color: #0f172a;
  border: 1px solid rgba(15,23,42,0.12);
  font-weight: 600;
}

:root.dark-version .sensor-hero {
  color: #e2e8f0;
  background: linear-gradient(135deg, rgba(0,212,255,0.1), rgba(30,64,175,0.18));
  border-color: rgba(0,212,255,0.24);
  box-shadow: 0 18px 40px rgba(0,0,0,0.4);
}

:root.dark-version .sensor-icon {
  background: rgba(255,255,255,0.12);
  color: #7dd3fc;
}

:root.dark-version .sensor-name-row h5 {
  color: #e2e8f0;
}

:root.dark-version .sensor-hero .text-sm {
  color: #cbd5e1;
}

:root.dark-version .meta-block {
  background: rgba(255,255,255,0.06);
  border-color: rgba(255,255,255,0.08);
  color: #e2e8f0;
}

:root.dark-version .meta-block .label {
  color: #cbd5e1;
}

:root.dark-version .pill {
  background: rgba(255,255,255,0.05);
  color: #e2e8f0;
  border-color: rgba(255,255,255,0.08);
}
</style>
